// frontend/src/context/AuthContext.js
import React, { createContext, useReducer, useContext, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext();

// --- Reducer ---
const initialState = {
    user: null,
    token: null,
    loading: true, // true mientras se verifica sessionStorage al inicio
};

function authReducer(state, action) {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                user: action.payload.user,
                token: action.payload.token,
                loading: false,
            };
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                token: null,
                loading: false,
            };
        case 'UPDATE_USER':
            return {
                ...state,
                user: action.payload,
            };
        case 'SET_LOADING':
            return {
                ...state,
                loading: action.payload,
            };
        default:
            return state;
    }
}

// --- Provider ---
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Inicializar desde sessionStorage al montar
    useEffect(() => {
        const token = sessionStorage.getItem('token');
        const userStr = sessionStorage.getItem('user');

        if (token && userStr) {
            try {
                const user = JSON.parse(userStr);
                dispatch({ type: 'LOGIN', payload: { user, token } });
            } catch (e) {
                console.error('Error parseando datos de usuario desde sessionStorage', e);
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('user');
                dispatch({ type: 'LOGOUT' });
            }
        } else {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    }, []);

    // Login
    const login = useCallback(async (email, password) => {
        const formData = new URLSearchParams();
        formData.append('username', email);
        formData.append('password', password);

        const response = await api.post('/auth/login/token', formData, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        });

        const token = response.data.access_token;
        sessionStorage.setItem('token', token);

        const userProfileResponse = await api.get('/users/me');
        const user = userProfileResponse.data;
        sessionStorage.setItem('user', JSON.stringify(user));

        dispatch({ type: 'LOGIN', payload: { user, token } });

        // Notificar a otros tabs/componentes que escuchan 'storage'
        window.dispatchEvent(new Event('storage'));
    }, []);

    // Logout
    const logout = useCallback(() => {
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        dispatch({ type: 'LOGOUT' });
        window.dispatchEvent(new Event('storage'));
    }, []);

    // Actualizar datos del usuario (ej: después de editar perfil)
    const updateUser = useCallback((userData) => {
        sessionStorage.setItem('user', JSON.stringify(userData));
        dispatch({ type: 'UPDATE_USER', payload: userData });
        window.dispatchEvent(new Event('storage'));
    }, []);

    const value = {
        user: state.user,
        token: state.token,
        loading: state.loading,
        isAuthenticated: !!state.token,
        isAdmin: state.user?.is_admin || false,
        login,
        logout,
        updateUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// --- Hook ---
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};
